import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { WorkspaceProvider, useWorkspace } from "@/context/WorkspaceContext";
import Logo from "@/components/Logo";
import { useFeatures } from "@/lib/features";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  House, Vault as VaultIcon, ShareNetwork, CreditCard, Gear, ShieldStar,
  SignOut, List, X, SealCheck, ChatCircleDots, PenNib, IdentificationCard, UsersThree, Scan, FilePdf, ShieldCheck,
  Pulse, Stack, FlowArrow, Heartbeat,
} from "@phosphor-icons/react";
import { useState } from "react";

const nav = [
  { to: "/dashboard", label: "Overview", icon: House, end: true },
  { to: "/dashboard/command", label: "Command Center", icon: Pulse, flag: "COMMAND_CENTER_ENABLED" },
  { to: "/dashboard/vault", label: "My Vault", icon: VaultIcon },
  { to: "/dashboard/rooms", label: "DuruRooms", icon: Stack, flag: "DURUROOMS_ENABLED" },
  { to: "/dashboard/flow", label: "DuruFlow", icon: FlowArrow, flag: "DURUFLOW_ENABLED" },
  { to: "/dashboard/pulse", label: "DuruPulse", icon: Heartbeat, flag: "DURU_PULSE_ENABLED" },
  { to: "/dashboard/scan", label: "DuruScan", icon: Scan, flag: "DURU_SCAN_ENABLED" },
  { to: "/dashboard/studio", label: "PDF Studio", icon: FilePdf, flag: "PDF_STUDIO_ENABLED" },
  { to: "/dashboard/organizations", label: "Teams", icon: UsersThree },
  { to: "/dashboard/shield", label: "DuruShield", icon: ShieldStar, flag: "DURU_SHIELD_ENABLED" },
  { to: "/dashboard/shares", label: "Shared Links", icon: ShareNetwork, flag: "DURU_SHIELD_ENABLED" },
  { to: "/dashboard/sign", label: "DuruSign", icon: PenNib, flag: "DURU_SIGN_ENABLED" },
  { to: "/dashboard/verify", label: "DuruVerify", icon: SealCheck, flag: "DURU_VERIFY_ENABLED" },
  { to: "/dashboard/checks", label: "ID Checks", icon: IdentificationCard, flag: "DURU_VERIFY_ENABLED" },
  { to: "/dashboard/verify-center", label: "Verification Center", icon: ShieldCheck, flag: "DURUVERIFY_AUTOMATED_ENABLED" },
  { to: "/dashboard/my-verified-documents", label: "My Verified Documents", icon: SealCheck },
  { to: "/dashboard/assistant", label: "Ask Your Vault", icon: ChatCircleDots },
  { to: "/dashboard/plans", label: "Plans & Storage", icon: CreditCard },
  { to: "/dashboard/settings", label: "Settings", icon: Gear },
];

function WorkspaceSwitcher() {
  const { orgs, active, setWorkspace } = useWorkspace();
  return (
    <div className="mt-6">
      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-medium mb-1.5 px-1">Workspace</p>
      <Select value={active} onValueChange={setWorkspace}>
        <SelectTrigger className="w-full bg-slate-50" data-testid="workspace-switcher"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="personal" data-testid="workspace-personal">Personal vault</SelectItem>
          {orgs.map((o) => (
            <SelectItem key={o.org_id} value={o.org_id} data-testid={`workspace-${o.org_id}`}>{o.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const flags = useFeatures();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const NavItems = () => (
    <>
      {nav.filter((n) => !n.flag || flags[n.flag]).map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-primary text-[#20242a]" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
          <n.icon size={20} weight="duotone" /> {n.label}
        </NavLink>
      ))}
      {user?.role === "admin" && (
        <NavLink to="/dashboard/admin" onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid="nav-admin">
          <ShieldStar size={20} weight="duotone" /> Admin
        </NavLink>
      )}
    </>
  );

  return (
    <WorkspaceProvider>
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-slate-200 bg-white p-5 fixed inset-y-0">
        <Logo to="/dashboard" />
        <WorkspaceSwitcher />
        <nav className="mt-6 space-y-1 flex-1 overflow-y-auto">{NavItems()}</nav>
        <div className="border-t border-slate-200 pt-4">
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 text-primary grid place-items-center font-semibold uppercase">
              {user?.name?.[0] || "U"}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} className="w-full justify-start text-slate-600" data-testid="logout-btn">
            <SignOut size={18} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 glass-light border-b border-slate-200 h-14 flex items-center justify-between px-4">
        <Logo to="/dashboard" />
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">{open ? <X size={24} /> : <List size={24} />}</button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-30 bg-white pt-16 px-5 space-y-1 overflow-y-auto">
          <WorkspaceSwitcher />
          <div className="mt-4 space-y-1">{NavItems()}</div>
          <Button variant="ghost" onClick={logout} className="w-full justify-start text-slate-600 mt-4"><SignOut size={18} className="mr-2" /> Sign out</Button>
        </div>
      )}

      <main id="main-content" tabIndex={-1} className="flex-1 lg:ml-64 pt-14 lg:pt-0 min-w-0 outline-none">
        <div className="max-w-6xl mx-auto p-5 sm:p-8">
          <Outlet />
        </div>
      </main>
    </div>
    </WorkspaceProvider>
  );
}
